import React, {FC, useEffect, useMemo, useState} from "react";
import {addressService} from "../../../store/actions";
import {Link, useParams} from "react-router-dom";
import useCommon from "../../../common/hooks/useCommon";
import {formatTimestamp, formatUnit, getFiat, getTimeLang, serializeQuery, toXDCNumber, truncate,toNumber} from "../../../common/utils/function";
import BTable2 from "../../../components/b-table2";
import AddressName from "../address-name";
import BTooltip from "../../../components/b-tooltip";
import {useTranslation} from "react-i18next";
import BCopy from "../../../components/b-copy";
import CSVConverter from "../../../components/b-CSV-converter";
import InOut from "../in-out";
import { useSelector } from "react-redux";


interface InternalTransactionAccountListProps {
    count?: number;
}

interface TransactionListData {
    transaction_hash: string;
    block: number;
    timestamp: string | number;
    type: any;
    from: any;
    to: any;
    value: string | number;
    gas_price: string | number;
}

const InternalTransactionAccountList: FC<
    InternalTransactionAccountListProps
> = (props) => {
    const {address} = useParams();
    const {getTime, getXDC, get0x} = useCommon();
    const {t, i18n} = useTranslation();
    const [isUTC, setIsUTC] = useState(false);
    const [isValue, setIsValue] = useState(false);
    const fiat = useSelector((e: Object) => (e as any)?.common?.fiat);
    const _stats = useSelector((e: Object) => (e as any)?.common?.stats);
    const [transactionList, setTransactionList] = useState<TransactionListData[]>(
        []
    );
    const [newTransactionList, setNewTransactionList] = useState<TransactionListData[]>(
        []
    );
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState<any>({
        limit: 50,
        page: 1,
    });
    const [totalRecords, setTotalRecords] = useState<any>(0);
    const handle = {
        pagination: (payload: any) => {
            setPagination(payload);
        }
    };

    const getBlocks = async () => {
        try {
            setLoading(true);
            addressService
                .getAddressInternalTransactions(
                    address,
                    serializeQuery({...pagination})
                )
                .then((response) => {
                    setTransactionList(response?.items);
                    const transformedItems = transformTransactions(response?.items);
                    setNewTransactionList(transformedItems)
                    setTotalRecords(response?.count ?? 0)
                    setLoading(false);
                })
                .catch((e) => setLoading(false));
        } catch (err) {
            console.log(err);
        }
    };
    const transformTransactions = (transactions: TransactionListData[]): TransactionListData[] => {
        return transactions.map((txn) => {
            const value = formatUnit(toXDCNumber(txn?.value, 18))
            const newVal = value.toString();
            return {
                ...txn,
                newVal,
            };
        });
    };

    let fileNameAddress = address || "unknown";
    const fileName = `export-internal-tx-${fileNameAddress}.csv`;

    const headerList = ['transaction_hash', 'block', 'timestamp', 'from.hash', 'to.hash', 'newVal'];
    const customHeaderList = {
        'transaction_hash': 'Parent Transaction Hash',
        'block': 'Block',
        'timestamp': 'Timestamp',
        'token.type': 'Type',
        'from.hash': 'From',
        'to.hash': 'To',
        'newVal': 'Value'
    }
    useEffect(() => {
        getBlocks();
    }, [pagination, address, isUTC, isValue]);
    const handleButtonClick = () => {
        setIsUTC(!isUTC);
    };

    const header = useMemo(() => {
        return [
            {
                title: `${t("Parent Transaction Hash")}`,
                param: "name",
                render: (data: any) => (
                    <React.Fragment>
            {data?.status === 0 ? (
                <BTooltip text={t("Error in Main Txn : execution reverted")}>
                    <strong>
                        <i
                            className="bi bi-exclamation-circle text-danger bold"
                            style={{fontWeight: "bold"}}
                        ></i>
                    </strong>
                </BTooltip>
            ) : null}{" "}
                        <p className="text-truncate">
              <Link to={`/tx/0x${get0x(data?.transaction_hash)}`}>
                0x{truncate(get0x(data?.transaction_hash))}
              </Link>
              <BCopy
                  value={data?.from?.hash}
                  tooltip="TxHash to clipboard"
              />
            </p>
                    </React.Fragment>
                ),
            },
            {
                title: `${t("Block")}`,
                param: "quantity",
                tooltip: true,
                render: (data: any) => (
                    <Link to={`/block/${data?.block}`}>{data?.block}</Link>
                ),

            },
            {
                title: (<a href="javascript:" onClick={handleButtonClick} data-bs-trigger="hover">{isUTC ? t("Date Time (UTC)") : t("Age")}</a>),
                param: "quantity",
                tooltip: true,
                render: (data: any) => isUTC ? formatTimestamp(data?.timestamp) : getTimeLang(data?.timestamp, t),
            },
            // {
            //     title: `${t("From")}`,
            //     param: "quantity",
            //     headerStyle: {minWidth: "130px"},
            //     tooltip: true,
            //     render: (data: any) => (
            //         <p className="text-truncate">
            //             <Link to={`/address/${get0x(data?.from?.hash)}`}>
            //                 {centerTruncate(getXDC(data?.from?.hash))}
            //             </Link>

            //         <BCopy
            //         value={data?.from?.hash}
            //         tooltip="From Address to clipboard"
            //     />
            //     </p>
            //     ),
            // },

            {
                title: `${t("From")}`,
                param: "from",
                headerStyle: {minWidth: "130px"},
                copy: (data: any) => getXDC(data?.from?.hash),
                tooltip: (data: any) => {
                    const fromHash = data?.from?.hash?.toLowerCase();
                    const isExcludedAddress = fromHash === address?.toLowerCase();

                    return isExcludedAddress ? (
                        <span>{getXDC(fromHash)}</span>
                    ) : (
                        <Link to={`/address/${get0x(fromHash)}`}>
                            {getXDC(fromHash)}
                        </Link>
                    );
                },
                render: (data: any) => {
                    const fromHash = data?.from?.hash?.toLowerCase();
                    const isExcludedAddress = fromHash === address?.toLowerCase();

                    return isExcludedAddress ? (
                        <AddressName data={data?.from}/>
                    ) : (
                        <Link to={`/address/${get0x(fromHash)}`}>
                            <AddressName data={data?.from}/>
                        </Link>
                    );
                }
            },
            {
                title: "",
                param: "from",
                render: (data: any) => (
                    <React.Fragment>
                        {get0x(data?.from?.hash)?.toLowerCase() === get0x(address)?.toLowerCase() ? <InOut out/> : <InOut/>}
                    </React.Fragment>
                ),
            },
            ,
            {
                title: `${t("To")}`,
                param: "to",
                headerStyle: {minWidth: "130px"},
                copy: (data: any) =>
                    !data?.to
                        ? getXDC(data?.created_contract?.hash)
                        : getXDC(data?.to?.hash),
                tooltip: (data: any) => {
                    const toHash = data?.to?.hash?.toLowerCase();
                    const createdContractHash = data?.created_contract?.hash?.toLowerCase();
                    const isExcludedToAddress = toHash === address?.toLowerCase();
                    const isExcludedContractAddress = createdContractHash === address?.toLowerCase();

                    if (!data?.to) {
                        return isExcludedContractAddress ? (
                            <span>{getXDC(createdContractHash)}</span>
                        ) : (
                            <Link to={`/address/${get0x(createdContractHash)}`}>
                                {getXDC(createdContractHash)}
                            </Link>
                        );
                    }

                    return isExcludedToAddress ? (
                        <span>{getXDC(toHash)}</span>
                    ) : (
                        <Link to={`/address/${get0x(toHash)}`}>
                            {getXDC(toHash)}
                        </Link>
                    );
                },
                render: (data: any) => {
                    const toHash = data?.to?.hash?.toLowerCase();
                    const createdContractHash = data?.created_contract?.hash?.toLowerCase();
                    const isExcludedToAddress = toHash === address?.toLowerCase();
                    const isExcludedContractAddress = createdContractHash === address?.toLowerCase();

                    if (!data?.to) {
                        return isExcludedContractAddress ? (
                            <AddressName data={data?.created_contract}/>
                        ) : (
                            <Link to={`/address/${get0x(createdContractHash)}`}>
                                <AddressName data={data?.created_contract}/>
                            </Link>
                        );
                    }

                    return isExcludedToAddress ? (
                        <AddressName data={data?.to}/>
                    ) : (
                        <Link to={`/address/${get0x(toHash)}`}>
                            <AddressName data={data?.to}/>
                        </Link>
                    );
                }
            },
            {
                title: (<a href="javascript:" onClick={() => setIsValue(!isValue)}data-bs-trigger="hover"> {isValue ? t("Value (USD)") : t("Amount")}</a>),
                param: "quantity",
                headerStyle: {minWidth: "130px"},
                tooltip: true,
                render: (data: any) =>
                <span onClick={() => setIsValue(!isValue)}>
                    {isValue  ?   getFiat(toXDCNumber(Number(_stats?.coin_price) * Number(data?.value)), 0, fiat, true): formatUnit(toXDCNumber(data?.value, 18)) }
                </span>,
            },
        ];
    }, [isUTC, isValue]);
    return (
        <BTable2
            title={<div className="d-block d-md-flex justify-content-between align-items-center">
                <p className="font-b"><i className="bi bi-sort-down me-1"></i>Latest 25 internal transactions (View All) <br/></p>
                <div className="my-2">
                    <CSVConverter data={newTransactionList} fileName={fileName} headers={headerList} customHeaders={customHeaderList}/>
                </div>
            </div>}
            header={header}
            list={transactionList}
            loading={loading}
            pagination={pagination}
            totalRecords={totalRecords}
            onPagination={handle.pagination}
        />
    );
};

export default InternalTransactionAccountList;